import React from 'react';
import queryString from 'query-string'


import axios from 'axios';

import './App.css';

import "react-loader-spinner/dist/loader/css/react-spinner-loader.css"
import Loader from 'react-loader-spinner'

// Google Analytics 4 events. The GA4 gtag.js snippet — and the automatic
// page_view — is loaded once by the Django base template, so here we only
// forward custom events through the global gtag(). No-ops when gtag is absent
// (e.g. running the CRA dev server standalone, outside Django).
function trackEvent(name, params) {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', name, params);
  }
}


// The book's product page in the shop. Used by the win-screen CTA.
const SHOP_URL = 'https://shop.eastinterpreter.com/products/japanese-word-search-learn-1-200-essential-japanese-words-completing-over-100-puzzles';

// Base URL for a word's full entry in the mrjisho dictionary.
const MRJISHO_WORD_URL = 'https://www.mrjisho.com/word/';

function getCategoryQueryParam() {
  let query = queryString.parse(window.location.search)
  let category = query.category;

  // Set a default if there is no param
  if (!category) {
    category = 'JLPT N5';
  }

  return category;
}

// Speak a Japanese string using the browser's built-in speech synthesis.
// No network/back-end needed; silently no-ops where unsupported.
function speak(text) {
  if (!text || typeof window === 'undefined' || !window.speechSynthesis) {
    return;
  }
  try {
    window.speechSynthesis.cancel();
    const utterance = new window.SpeechSynthesisUtterance(text);
    utterance.lang = 'ja-JP';
    utterance.rate = 0.85;
    window.speechSynthesis.speak(utterance);
  } catch (e) {
    // Speech synthesis can throw on some browsers; ignore.
  }
}

// Whether to auto-pronounce a word the moment it's found. Defaults to off;
// remembered across visits via localStorage.
function getAutoSpeakPref() {
  try {
    return window.localStorage.getItem('autoSpeak') === 'true';
  } catch (e) {
    return false;
  }
}

// Format a number of milliseconds as m:ss.
function formatElapsed(ms) {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

// Given a start and end cell ([col, row]), return the list of cells along the
// straight line between them, but only when the two cells are aligned
// horizontally, vertically, or diagonally (the only ways words are placed).
// Otherwise just the start cell is returned so the highlight doesn't mislead.
function computePath(start, end) {
  if (!start) {
    return [];
  }
  if (!end) {
    return [start];
  }

  const dCol = end[0] - start[0];
  const dRow = end[1] - start[1];

  const aligned =
    dCol === 0 ||
    dRow === 0 ||
    Math.abs(dCol) === Math.abs(dRow);

  if (!aligned) {
    return [start];
  }

  const steps = Math.max(Math.abs(dCol), Math.abs(dRow));
  const stepCol = Math.sign(dCol);
  const stepRow = Math.sign(dRow);

  const path = [];
  for (let i = 0; i <= steps; i++) {
    path.push([start[0] + i * stepCol, start[1] + i * stepRow]);
  }
  return path;
}

class App extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      puzzle: [],
      words: [],
      wordList: [],
      selectionPath: [],
      found: [],
      categories: ['JLPT N5', 'JLPT N4', 'JLPT N3', 'JLPT N2', 'JLPT N1', 'Business', 'Culture', 'Diplomacy', 'Economics', 'Education', 'Health', 'International Relations', 'Judiciary', 'Labor', 'Mass Media', 'Military', 'Natural Disasters', 'Politics', 'Primary Industries', 'Secondary Industries', 'Science', 'Sports', 'Technology', 'Transportation', 'Travel'],
      activeCategory: getCategoryQueryParam(),
      loading: false,
      gameCompleted: false,
      isDragging: false,
      dragStart: null,
      startTime: null,
      completedAt: null,
      now: Date.now(),
      hintCell: null,
      hintsUsed: 0,
      autoSpeak: getAutoSpeakPref(),
    };
    this.handleChange = this.handleChange.bind(this);
    this.startSelection = this.startSelection.bind(this);
    this.extendSelection = this.extendSelection.bind(this);
    this.endSelection = this.endSelection.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.showHint = this.showHint.bind(this);
    this.toggleAutoSpeak = this.toggleAutoSpeak.bind(this);
  }

  componentDidMount() {
    this.getPuzzle();

    // Releasing the mouse anywhere (even off the board) ends a selection.
    window.addEventListener('mouseup', this.endSelection);

    // Tick the clock once a second so the timer updates while playing.
    this.timerId = setInterval(() => {
      if (!this.state.gameCompleted && this.state.startTime) {
        this.setState({ now: Date.now() });
      }
    }, 1000);
  }

  componentWillUnmount() {
    window.removeEventListener('mouseup', this.endSelection);
    if (this.timerId) {
      clearInterval(this.timerId);
    }
    if (this.hintTimeout) {
      clearTimeout(this.hintTimeout);
    }
  }

  getEndpoint() {

    let endpoint = process.env.REACT_APP_ENDPOINT || '/puzzle/';

    if (this.state.activeCategory) {
      endpoint += '?category=' + this.state.activeCategory;
    }

    return endpoint;
  }

  getPuzzle() {
    const endpoint = this.getEndpoint();
    this.setState({loading: true});
    axios
      .get(endpoint)
      .then(res => {
        this.createPuzzle(res.data);
        this.setState({loading:false});
      })
      .catch(err => {
        this.setState({loading:false});
      });
  }

  // Add a word to the found list, remove it from the remaining words, and
  // check whether that completes the puzzle. Runs in a single immutable
  // update so completion is evaluated against fresh state.
  processFoundWord(word) {
    if (this.state.autoSpeak) {
      speak(word['word']);
    }

    this.setState(prevState => {
      const found = [...prevState.found, word];
      const words = prevState.words.filter(w => w['word'] !== word['word']);
      const gameCompleted = found.length === prevState.wordList.length;

      return {
        found,
        words,
        selectionPath: [],
        gameCompleted,
        completedAt: gameCompleted ? Date.now() : prevState.completedAt,
      };
    }, () => {
      trackEvent('word_found', { category: this.state.activeCategory });
      if (this.state.gameCompleted) {
        trackEvent('puzzle_completed', { category: this.state.activeCategory });
      }
    });
  }

  // Compare a selection (start & end cell) against every remaining word.
  matchSelection(coordinateSet) {
    const wordsToFind = this.state.words;
    for (let i = 0; i < wordsToFind.length; i++) {
      let word = wordsToFind[i];
      let letters = word['map'];
      let firstCoordinate = [letters[0]['x'], letters[0]['y']];
      let lastCoordinate = [letters[letters.length-1]['x'], letters[letters.length-1]['y']];

      // One-letter word: a single-cell selection that lands on it.
      if (coordinateSet.length === 1) {
        if (JSON.stringify(firstCoordinate) === JSON.stringify(lastCoordinate) &&
            JSON.stringify(firstCoordinate) === JSON.stringify(coordinateSet[0])) {
          this.processFoundWord(word);
        }
        continue;
      }

      // Two-cell selection: match the word forwards or backwards.
      if (coordinateSet.length === 2 &&
          (JSON.stringify(coordinateSet) === JSON.stringify([firstCoordinate, lastCoordinate]) ||
           JSON.stringify(coordinateSet) === JSON.stringify([lastCoordinate, firstCoordinate]))) {
        this.processFoundWord(word);
      }
    }
  }

  // --- Drag selection handlers ---------------------------------------------

  startSelection(coordinate) {
    this.setState({
      isDragging: true,
      dragStart: coordinate,
      selectionPath: [coordinate],
    });
  }

  extendSelection(coordinate) {
    if (!this.state.isDragging) {
      return;
    }
    this.setState({
      selectionPath: computePath(this.state.dragStart, coordinate),
    });
  }

  endSelection() {
    if (!this.state.isDragging) {
      return;
    }

    const path = this.state.selectionPath;
    if (path.length >= 1) {
      const start = path[0];
      const end = path[path.length - 1];
      const coordinateSet =
        (start[0] === end[0] && start[1] === end[1]) ? [start] : [start, end];
      this.matchSelection(coordinateSet);
    }

    this.setState({
      isDragging: false,
      dragStart: null,
      selectionPath: [],
    });
  }

  // Briefly flash the first letter of a random unfound word, and count it.
  showHint() {
    const unfound = this.state.words;
    if (!unfound.length) {
      return;
    }
    const word = unfound[Math.floor(Math.random() * unfound.length)];
    const firstCell = [word['map'][0]['x'], word['map'][0]['y']];

    if (this.hintTimeout) {
      clearTimeout(this.hintTimeout);
    }
    this.setState(prev => ({ hintCell: firstCell, hintsUsed: prev.hintsUsed + 1 }));
    trackEvent('hint_used', { category: this.state.activeCategory });

    this.hintTimeout = setTimeout(() => this.setState({ hintCell: null }), 1800);
  }

  // Toggle auto-pronouncing a word when it's found, and remember the choice.
  toggleAutoSpeak() {
    this.setState(prev => {
      const autoSpeak = !prev.autoSpeak;
      try {
        window.localStorage.setItem('autoSpeak', autoSpeak);
      } catch (e) {
        // localStorage may be unavailable (private mode); ignore.
      }
      return { autoSpeak };
    });
  }

  // Touch dragging: find whichever cell is under the finger and extend to it.
  handleTouchMove(e) {
    if (!this.state.isDragging) {
      return;
    }
    const touch = e.touches[0];
    if (!touch) {
      return;
    }
    const el = document.elementFromPoint(touch.clientX, touch.clientY);
    if (el && el.dataset && el.dataset.col !== undefined) {
      this.extendSelection([parseInt(el.dataset.col, 10), parseInt(el.dataset.row, 10)]);
    }
  }

  // Handle when the user changes the Category dropdown
  handleChange(e) {
    this.setState({activeCategory: e.target.value}, () => {
      this.getPuzzle();
    });
  }

  createPuzzle(response) {
    let puzzle = [];
    let puzzleString = response.puzzle;
    let puzzleCharCounter = 0;

    for (let x = 0; x < parseInt(response.height); x++ ) {
      puzzle[x] = [];
      for (let y = 0; y < parseInt(response.width); y++ ) {
        puzzle[x][y] = puzzleString.charAt(puzzleCharCounter);
        puzzleCharCounter++;
      }
    }

    this.setState({
      width: parseInt(response.width),
      height: parseInt(response.height),
      words: response.words,
      wordList: response.words,
      puzzle: puzzle,
      selectionPath: [],
      found: [],
      gameCompleted: false,
      isDragging: false,
      dragStart: null,
      startTime: Date.now(),
      completedAt: null,
      now: Date.now(),
      hintCell: null,
      hintsUsed: 0,
    });

    if (this.hintTimeout) {
      clearTimeout(this.hintTimeout);
    }

    // Set the url with the category query param
    let query = '?' + queryString.stringify({category:this.state.activeCategory});
    window.history.pushState(null, null, query)

    // Notify Google Analytics that a puzzle was loaded
    trackEvent('puzzle_loaded', { category: this.state.activeCategory });
  }

  render() {

    // Build the word list rows.
    let wordLis = [];
    this.state.wordList.forEach((word, i) => {
      const isFound = this.state.found.some(w => w['word'] === word['word']);
      wordLis.push(<WordLi
        key={i}
        word={word['word']}
        meaning={word['meaning']}
        reading={word['reading']}
        found={isFound}
      />);
    });

    // Build the board.
    let board = [];
    let buttons = [];
    let puzzleCharCounter = 1;

    for (let x = 0; x < this.state.height; x++ ) {
      for (let y = 0; y < this.state.width; y++ ) {
        let letter = this.state.puzzle[x][y];

        let foundClasses = '';
        this.state.found.forEach(word => {
          word['map'].forEach(letterCell => {
            if (letterCell['y'] === x && letterCell['x'] === y) {
              foundClasses += 'found ';
            }
          });
        });

        // Highlight cells in the active drag selection.
        if (this.state.selectionPath.some(c => c[0] === y && c[1] === x)) {
          foundClasses += 'selecting ';
        }

        // Flash the hinted first-letter cell.
        if (this.state.hintCell && this.state.hintCell[0] === y && this.state.hintCell[1] === x) {
          foundClasses += 'hint ';
        }

        buttons.push(<Square
          key={puzzleCharCounter.toString()}
          letter={letter}
          coordinate={[y, x]}
          classes={foundClasses}
          onStart={this.startSelection}
          onExtend={this.extendSelection}
        />);

        if (puzzleCharCounter === (this.state.height*this.state.width) ||
            puzzleCharCounter % parseInt(this.state.width) === 0) {
          board.push(<div className="board-row" key={puzzleCharCounter.toString()}>{buttons}</div>);
          buttons = [];
        }

        puzzleCharCounter++;
      }
    }

    const foundCount = this.state.found.length;
    const totalCount = this.state.wordList.length;
    const progressPct = totalCount ? Math.round((foundCount / totalCount) * 100) : 0;
    const elapsedMs = (this.state.completedAt || this.state.now) - (this.state.startTime || this.state.now);

    return (
    <div>
      <div className="row text-xs-center">
          <div className="col-lg-7 col-xl-6 text-center">
            <div className="puzzle-status">
              <span className="progress-count">{foundCount} / {totalCount} found</span>
              <button
                type="button"
                className="hint-btn"
                onClick={this.showHint}
                disabled={this.state.gameCompleted || this.state.loading || this.state.words.length === 0}
              >
                💡 Hint{this.state.hintsUsed > 0 ? ` (${this.state.hintsUsed})` : ''}
              </button>
              <span className="timer">⏱ {formatElapsed(elapsedMs)}</span>
            </div>
            <div className="progress puzzle-progress">
              <div
                className="progress-bar"
                role="progressbar"
                style={{ width: `${progressPct}%` }}
                aria-valuenow={progressPct}
                aria-valuemin="0"
                aria-valuemax="100"
              ></div>
            </div>

            <div className="board-wrap">
              <div
                className={"board" + (this.state.loading ? " is-loading" : "")}
                onTouchMove={this.handleTouchMove}
                onTouchEnd={this.endSelection}
              >
                {board}
              </div>
              {this.state.loading &&
                <div className="board-loading">
                  <Loader type="Oval" color="#00BFFF" height={70} width={70} timeout={12000} />
                  <div className="board-loading-text">Loading puzzle…</div>
                </div>
              }
            </div>
            <div className="board-hint">Drag across the letters to select a word.</div>
          </div>
          <div className="col-lg-5 col-xl-5">
            <div className="dropdown-category">
              Choose category: &nbsp;&nbsp;
              <Dropdown
                options={this.state.categories}
                handleChange={this.handleChange}
                selectedValue={this.state.activeCategory}
              />
            </div>
            <label className="autospeak-toggle">
              <input
                type="checkbox"
                checked={this.state.autoSpeak}
                onChange={this.toggleAutoSpeak}
              />
              &nbsp;🔊 Say each word out loud when found
            </label>
            <div className="wordlist">
              <table className="table table-striped">
                <thead>
                  <tr><th>Japanese</th><th>Meaning</th><th></th></tr>
                </thead>
                <tbody>
                  {wordLis}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {this.state.gameCompleted &&
          <WinScreen
            words={this.state.wordList}
            elapsed={formatElapsed(elapsedMs)}
            hintsUsed={this.state.hintsUsed}
            onPlayAgain={() => this.getPuzzle()}
          />
        }
    </div>
    );
  }
}

function Square(props) {
  const [col, row] = props.coordinate;
  return (
    <div
      className={"square " + props.classes}
      data-col={col}
      data-row={row}
      onMouseDown={(e) => { e.preventDefault(); props.onStart(props.coordinate); }}
      onMouseEnter={() => props.onExtend(props.coordinate)}
      onTouchStart={(e) => { e.preventDefault(); props.onStart(props.coordinate); }}
    >
      {props.letter}
    </div>
  );
}

function WordLi(props) {
  return (
    <tr className={props.found ? 'found-word' : ''}>
      <td className="reading">
        {props.reading}
        <div className="word">{props.word}</div>
      </td>
      <td className="align-middle meaning">{props.meaning}</td>
      <td className="align-middle audio-cell">
        <button
          type="button"
          className="audio-btn"
          aria-label={"Play pronunciation of " + props.word}
          onClick={() => speak(props.word)}
        >
          🔊
        </button>
      </td>
    </tr>
  );
}

function WinScreen(props) {
  return (
    <div className="win-overlay">
      <div className="win-card">
        <div className="win-emoji">🎉</div>
        <h2>Puzzle complete!</h2>
        <p className="win-time">
          You found all {props.words.length} words in {props.elapsed}.
          {props.hintsUsed > 0
            ? ` Hints used: ${props.hintsUsed}.`
            : ' No hints used! 🌟'}
        </p>
        <p className="win-wordlist-hint">📖 Tap any word for its full entry in the dictionary ↗</p>
        <ul className="win-wordlist">
          {props.words.map((w, i) => (
            <li key={i}>
              <button
                type="button"
                className="audio-btn"
                aria-label={"Play pronunciation of " + w['word']}
                onClick={() => speak(w['word'])}
              >
                🔊
              </button>
              {w['slug']
                ? <a
                    className="win-word win-word-link"
                    href={MRJISHO_WORD_URL + encodeURIComponent(w['slug'])}
                    target="_blank"
                    rel="noopener"
                    onClick={() => trackEvent('dictionary_click', { location: 'win_screen' })}
                  >{w['word']}</a>
                : <span className="win-word">{w['word']}</span>}
              {w['reading'] ? <span className="win-reading">（{w['reading']}）</span> : null}
              <span className="win-meaning">{w['meaning']}</span>
            </li>
          ))}
        </ul>
        <a
          className="win-shop-cta"
          href={SHOP_URL}
          target="_blank"
          rel="noopener"
          onClick={() => trackEvent('shop_cta_click', { location: 'win_screen' })}
        >
          📕 Enjoying these? Get 100+ puzzles in the printed book →
        </a>
        <button type="button" className="btn btn-primary btn-lg win-again" onClick={props.onPlayAgain}>
          Play again
        </button>
      </div>
    </div>
  );
}

function Dropdown(props) {

  const options = [];
  props.options.forEach((option, i) => {
    options.push(<option key={i} value={option}>{option}</option>);
  });

  return (
    <select value={props.selectedValue} onChange={props.handleChange}>
      {options}
    </select>
  );
}

export default App;
